import { Button, Modal, SlideDown, Typography } from '@globalmed/atlas-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import styles from './IncomingCallModal.module.css';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useWindowBlankAction } from '@/hooks/useWindowBlankAction';
import { useAppointmentsStore } from '@/stores/appointments';
import { useCallRequestsStore } from '@/stores/callRequests';
import { useInstitutionsStore } from '@/stores/institutions';
import { useToastStore } from '@/stores/toast';
import { ERouterNames, TCallRequestCamel } from '@/types';
import { calculateAge } from '@/utils/convertDate';
import { nameFormat } from '@/utils/valuesFormat';

type TProps = {
  callRequest: TCallRequestCamel;
};

/**
 * The Incoming Session modal of a user a presenter is calling into their
 * session. Get Started accepts the call and opens the session in a new tab:
 * its Waiting Room when it has one, even an ended one, and the video call
 * otherwise. The
 * modal goes away once the call is over, however it ended. Renders as a
 * bottom sheet on small screens and a centered dialog otherwise.
 * @param props - Component props.
 * @param props.callRequest - The ringing call.
 * @returns The modal.
 */
export const IncomingCallModal = ({ callRequest }: TProps) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');
  const ModalComponent = isSmallScreen ? SlideDown : Modal;
  const [isDeclining, setIsDeclining] = useState(false);
  const { acceptCallRequest, declineCallRequest } = useCallRequestsStore(
    (state) => ({
      acceptCallRequest: state.acceptCallRequest,
      declineCallRequest: state.declineCallRequest,
    }),
  );
  const getAppointment = useAppointmentsStore((state) => state.getAppointment);
  const getService = useInstitutionsStore((state) => state.getService);
  const setToast = useToastStore((state) => state.setToast);
  const { runWithWindow, loading } = useWindowBlankAction();

  const patient = callRequest.appointment?.patient;
  const service =
    getService(callRequest.appointment?.institutionServiceId ?? '')?.name ??
    t('dashboard.unknown-service');
  const patientName =
    patient &&
    (patient.dob
      ? t('call-request.patient-age', {
          name: nameFormat(patient.firstName, patient.lastName),
          age: calculateAge(patient.dob),
        })
      : nameFormat(patient.firstName, patient.lastName));

  const onError = (error: unknown) => {
    console.error(error);
    setToast({
      title: t('something-went-wrong-please-try-again'),
      variant: 'error',
    });
  };

  const onDecline = async () => {
    setIsDeclining(true);
    try {
      await declineCallRequest(callRequest.id);
    } catch (error) {
      onError(error);
    } finally {
      setIsDeclining(false);
    }
  };

  const onGetStarted = () =>
    runWithWindow(async (window) => {
      await acceptCallRequest(callRequest.id);
      const { waitingRoom, chatId } = await getAppointment(
        callRequest.appointmentId,
      );
      window.location.href = waitingRoom?.id
        ? generatePath(`/${ERouterNames.WAITING_ROOM}`, {
            waitingRoomId: waitingRoom.id,
          })
        : generatePath(`/${ERouterNames.ENCOUNTER}`, { chatId });
    }, onError);

  return (
    <ModalComponent
      open
      className={styles.modal}
      sx={isSmallScreen ? { width: '100%' } : { width: '360px' }}
      title={
        <Typography as="span" fontSize="20px" fontWeight={600}>
          {t('call-request.incoming-session')}
        </Typography>
      }
      body={
        <div className={styles.container}>
          <Typography textAlign="center">
            {service}
            {patientName && (
              <>
                : <strong>{patientName}</strong>
              </>
            )}
          </Typography>
          <div className={styles.buttons}>
            <Button
              variant="secondary"
              color="destructive"
              wide
              loading={isDeclining}
              disabled={loading}
              text={t('decline')}
              onClick={onDecline}
            />
            <Button
              color="success"
              wide
              loading={loading}
              disabled={isDeclining}
              text={t('encounter.get-started')}
              onClick={onGetStarted}
            />
          </div>
        </div>
      }
    />
  );
};
